import { Input as FluentInput, InputProps as FluentInputProps } from '@fluentui/react-components';
import { CheckmarkCircleRegular, ErrorCircleRegular } from '@fluentui/react-icons';
import cn from 'classnames';
import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useInputAppearance } from '../hooks';
import { Tooltip } from '../Tooltip';
import './Input.scss';

export type InputValidationState = 'none' | 'success' | 'warning' | 'error';

export type InputProps = FluentInputProps & {
  validationState?: InputValidationState;
  /**
   * Message surfaced as the hover tooltip (and `aria-label`) of the icon shown for
   * `validationState="error"`. Use it to explain what is wrong without adding a
   * message row that would shift the layout. When omitted, a generic message is used.
   */
  validationMessage?: string;
};

/**
 * Themed Input wrapper that automatically applies the correct
 * filled appearance based on the current theme (dark/light).
 * The appearance can still be overridden via props.
 *
 * Supports `validationState`:
 * - `"success"` shows a green checkmark when the input has a value and is not disabled.
 * - `"error"` shows a red error icon; pass `validationMessage` to explain the problem
 *   via the icon's tooltip/aria-label. The icon lives inside the input, so toggling it
 *   does not change the field height.
 *
 * An explicit `contentAfter` prop takes precedence over the validation icon.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ appearance, validationState, validationMessage, contentAfter, className, ...props }, ref) => {
    const themedAppearance = useInputAppearance();
    const { t } = useTranslation();

    const canShowValidationIcon = !props.disabled && !contentAfter;

    let validationIcon: React.ReactElement | undefined;
    if (canShowValidationIcon && validationState === 'success') {
      validationIcon = <CheckmarkCircleRegular className="inputSuccessIcon" />;
    } else if (canShowValidationIcon && validationState === 'error') {
      const errorMessage =
        validationMessage ??
        t('input_validation_error_generic', { defaultValue: 'Ungültige Eingabe' });
      validationIcon = (
        <Tooltip
          relationship="inaccessible"
          content={errorMessage}
          trigger={<ErrorCircleRegular className="inputErrorIcon" aria-label={errorMessage} />}
        />
      );
    }

    return (
      <FluentInput
        ref={ref}
        className={cn('Input', className)}
        appearance={appearance ?? themedAppearance}
        contentAfter={contentAfter ?? validationIcon}
        {...props}
      />
    );
  },
);

Input.displayName = 'Input';
